import { useState } from 'react';
import { Plus, GripVertical } from 'lucide-react';
import type { ExperienceJSON, ExperienceElement } from '../types';

interface SidebarProps {
  layoutIds: string[];
  currentLayoutId: string;
  experience: ExperienceJSON;
  getTriggerConflict: (layoutId: string) => string | null;
  setCurrentLayoutId: (id: string) => void;
  setSelectedElementId: (id: string | null) => void;
  handleCreateLayout: () => void;
  showAddMenu: boolean;
  setShowAddMenu: (show: boolean) => void;
  handleAddElement: (type: 'text' | 'image' | 'video' | 'button' | 'view') => void;
  elements: ExperienceElement[];
  selectedElementId: string | null;
  onReorderElements: (fromIndex: number, toIndex: number) => void;
}

export function Sidebar({
  layoutIds,
  currentLayoutId,
  experience,
  getTriggerConflict,
  setCurrentLayoutId,
  setSelectedElementId,
  handleCreateLayout,
  showAddMenu,
  setShowAddMenu,
  handleAddElement,
  elements,
  selectedElementId,
  onReorderElements,
}: SidebarProps) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragEnd = () => {
    if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
      onReorderElements(draggedIndex, dragOverIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div className="eb-panel eb-layers-panel">
      {/* Layout Selector */}
      <div className="layout-selector">
        <div className="layout-selector-header">
          <span className="layout-label">Layout</span>
          <button className="icon-btn" onClick={handleCreateLayout} title="New Layout">
            <Plus size={14} />
          </button>
        </div>
        <div className="layout-tabs">
          {layoutIds.map(id => {
            const layout = experience.layouts[id];
            const trigger = layout?.trigger;
            const hasConflict = id !== 'default' && getTriggerConflict(id) !== null;
            // Build trigger badge text
            let triggerBadge = null;
            if (trigger) {
              if (trigger.event === 'trackStart' && trigger.trackId !== undefined) {
                triggerBadge = `Track ${trigger.trackId}`;
              } else if (trigger.event === 'playbackPosition' && trigger.time !== undefined) {
                triggerBadge = `@${trigger.time}s`;
              }
            }
            return (
              <div
                key={id}
                className={`layout-tab ${currentLayoutId === id ? 'active' : ''} ${hasConflict ? 'has-conflict' : ''}`}
                onClick={() => {
                  setCurrentLayoutId(id);
                  setSelectedElementId(null);
                }}
              >
                <span>{id}</span>
                {triggerBadge && (
                  <span className={`layout-trigger-badge ${hasConflict ? 'conflict' : ''}`} title={hasConflict ? `Conflict: same trigger as another layout` : `Trigger: ${triggerBadge}`}>
                    {triggerBadge}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="panel-header">
        <h3>Layers</h3>
        <button className="icon-btn" onClick={() => setShowAddMenu(!showAddMenu)}>
          <Plus size={14} />
        </button>
      </div>

      {showAddMenu && (
        <div className="add-menu">
          <button onClick={() => handleAddElement('text')}>Text</button>
          <button onClick={() => handleAddElement('image')}>Image</button>
          <button onClick={() => handleAddElement('video')}>Video</button>
          <button onClick={() => handleAddElement('button')}>Button</button>
          <button onClick={() => handleAddElement('view')}>View</button>
        </div>
      )}

      <div className="layers-list">
        {elements.length === 0 ? (
          <p className="placeholder">No elements yet</p>
        ) : (
          [...elements].reverse().map((element, reversedIndex) => {
            // Original index for drag/drop ordering (layers at top = highest z-index = end of array)
            const index = elements.length - 1 - reversedIndex;
            return (
            <div
              key={element.id}
              className={`layer-item ${selectedElementId === element.id ? 'selected' : ''} ${draggedIndex === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
              onClick={() => setSelectedElementId(element.id)}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              onDragLeave={() => setDragOverIndex(null)}
            >
              <span className="layer-drag-handle" title="Drag to reorder">
                <GripVertical size={12} />
              </span>
              <span className="layer-type">{element.type}</span>
              <span className="layer-id">{element.id}</span>
              {element.animations && element.animations.length > 0 && (
                <span className="layer-animated" title="Has animations">✦</span>
              )}
            </div>
          );
          })
        )}
      </div>
    </div>
  );
}
